import * as React from 'react';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { CopyButton, FormatAmount } from 'components';
import {
  FAUCET_AMOUNT_EGLD,
  FAUCET_API_URL,
  FAUCET_WALLET_ADDRESS
} from 'config';
import { useGetAccount, useGetIsLoggedIn, useGetNetworkConfig } from 'hooks';
import { routeNames } from 'routes';

const DEVNET_EXPLORER_ADDRESS = 'http://devnet-explorer.multiversx.com';
const FAUCET_BALANCE_REFRESH_MS = 30000;

interface ClaimSuccessResponse {
  message: string;
  txHash: string;
}

interface ClaimErrorResponse {
  error: string;
  reasonCode?: string;
  nextClaimAt?: string;
}

const Faucet = () => {
  const isLoggedIn = useGetIsLoggedIn();
  const { address } = useGetAccount();
  const { network } = useGetNetworkConfig();

  const [isClaiming, setIsClaiming] = React.useState(false);
  const [txHash, setTxHash] = React.useState<string>();
  const [error, setError] = React.useState<ClaimErrorResponse>();
  const [faucetBalance, setFaucetBalance] = React.useState<string>();

  React.useEffect(() => {
    const fetchFaucetBalance = async () => {
      try {
        const { data } = await axios.get<{ balance: string }>(
          `/accounts/${FAUCET_WALLET_ADDRESS}`,
          { baseURL: network.apiAddress }
        );
        setFaucetBalance(data.balance);
      } catch (err) {
        console.error('Unable to fetch faucet wallet balance', err);
      }
    };

    fetchFaucetBalance();
    const interval = setInterval(fetchFaucetBalance, FAUCET_BALANCE_REFRESH_MS);
    return () => clearInterval(interval);
  }, [network.apiAddress]);

  const handleClaim = async () => {
    setIsClaiming(true);
    setError(undefined);
    setTxHash(undefined);

    try {
      const { data } = await axios.post<ClaimSuccessResponse>(
        '/faucet/claim',
        {},
        { baseURL: FAUCET_API_URL }
      );
      setTxHash(data.txHash);
    } catch (err) {
      const response = (err as AxiosError<ClaimErrorResponse>).response;
      setError(
        response?.data ?? {
          error: 'Unable to reach the faucet, please retry later.'
        }
      );
    }

    setIsClaiming(false);
  };

  return (
    <div className='container py-4'>
      <div className='row'>
        <div className='col-12 col-md-8 col-lg-6 mx-auto'>
          <div className='card shadow-sm rounded p-4 border-0 text-center'>
            <h2 className='mb-3' data-testid='title'>
              Devnet Faucet
            </h2>
            <p className='mb-2'>
              Claim {FAUCET_AMOUNT_EGLD} xEGLD on the MultiversX devnet, once
              every 24 hours.
            </p>
            <p className='text-muted mb-4'>
              <a
                href={`${DEVNET_EXPLORER_ADDRESS}/accounts/${FAUCET_WALLET_ADDRESS}`}
                target='_blank'
                rel='noreferrer'
              >
                Faucet balance
              </a>
              : {faucetBalance ? <FormatAmount value={faucetBalance} /> : '…'}
            </p>

            {!isLoggedIn && (
              <>
                <p className='mb-3'>Connect your wallet to claim.</p>
                <Link
                  to={routeNames.unlock}
                  className='btn btn-primary'
                  data-testid='loginBtn'
                >
                  Login
                </Link>
              </>
            )}

            {isLoggedIn && (
              <>
                <p className='text-muted mb-4' data-testid='accountAddress'>
                  {address}
                </p>
                <Button
                  variant='primary'
                  onClick={handleClaim}
                  disabled={isClaiming}
                  data-testid='btnClaimFaucet'
                >
                  {isClaiming ? 'Claiming…' : 'Claim xEGLD'}
                </Button>
              </>
            )}

            {txHash && (
              <div className='alert alert-success mt-4 text-break'>
                Faucet claimed!{' '}
                <a
                  href={`${DEVNET_EXPLORER_ADDRESS}/transactions/${txHash}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  View transaction
                </a>
                <CopyButton className='ms-2' text={txHash} />
              </div>
            )}

            {error && (
              <div className='alert alert-warning mt-4' role='alert'>
                {error.error}
                {error.nextClaimAt && (
                  <>
                    <br />
                    Next claim available{' '}
                    {moment(error.nextClaimAt).format('DD/MM/YYYY HH:mm')} (
                    {moment(error.nextClaimAt).fromNow()})
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faucet;
